// src/pages/butcher/ButcherDashboard.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../customer/useCart';
import api from '../../utils/api';

// ── We'll reuse customer panels where logic is identical ──
import BrowsePanel    from '../customer/customerpanel/BrowsePanel';
import CartPanel      from '../customer/customerpanel/CartPanel';

// ── Butcher-specific panels (create these next) ──
import OverviewPanel     from './butcherpanel/OverviewPanel';
import OrdersPanel       from './butcherpanel/OrdersPanel';
import ShopPanel         from './butcherpanel/ShopPanel';
import AddProductPanel   from './butcherpanel/AddProductPanel';
import MyShopOrdersPanel from './butcherpanel/MyShopOrdersPanel';
import InquiriesPanel    from './butcherpanel/InquiriesPanel';
import BulkOrdersPanel   from './butcherpanel/BulkOrdersPanel';
import PaymentsPanel     from './butcherpanel/PaymentsPanel';
import ProfilePanel      from './butcherpanel/ProfilePanel';

const NAV = [
  { key: 'overview',    icon: 'dashboard',        label: 'Overview'      },
  { key: 'browse',      icon: 'search',           label: 'Buy Animals'   },
  { key: 'bulk',        icon: 'inventory_2',      label: 'Bulk Orders'   },
  { key: 'orders',      icon: 'receipt_long',     label: 'My Orders'     },
  { key: 'shop',        icon: 'storefront',       label: 'My Shop'       },
  { key: 'add_product', icon: 'add_box',          label: 'Add Product'   },
  { key: 'shop_orders', icon: 'storefront',        label: 'Shop Orders'  },
  { key: 'inquiries',   icon: 'forum',            label: 'Inquiries'     },
  { key: 'payments',    icon: 'payments',         label: 'Payments'      },
  { key: 'cart',        icon: 'shopping_cart',    label: 'Cart'          },
  { key: 'profile',     icon: 'person',           label: 'Profile'       },
];

export default function ButcherDashboard() {
  const navigate = useNavigate();
  const { user, logout, loading: authLoading } = useAuth();
  const { cart } = useCart(user);

  const [active, setActive] = useState(() =>
    window.location.hash.replace('#', '') || 'overview'
  );
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showNotifs,  setShowNotifs]  = useState(false);
  const [notifList,   setNotifList]   = useState([]);

  const fetchNotifications = useCallback(async () => {
    if (!user?.id) return;
    try {
      const { data } = await api.get('/api/butcher/notifications');
      setNotifList(data);
    } catch (err) {
      console.error('fetchNotifications error:', err);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 10000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const unreadCount = notifList.filter(n => !n.is_read).length;

  const setActiveTab = (tab) => {
    window.location.hash = tab;
    setActive(tab);
  };

  useEffect(() => {
    const onPopState = () => {
      if (!window.location.pathname.startsWith('/butcher')) return;
      const tab = window.location.hash.replace('#', '') || 'overview';
      setActive(tab);
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  // ── Auth guard ─────────────────────────────────────────
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (authLoading) return;
    if (!user) navigate('/login');
    else if (user.role !== 'butcher') navigate('/');
  }, [user, navigate, authLoading]);

  if (authLoading) return null;

  const handleLogout = () => { logout(); navigate('/'); };

  // ── Panel map ──────────────────────────────────────────
  const PANELS = {
    overview:    <OverviewPanel   user={user} onNavigate={setActiveTab} />,
    browse:      <BrowsePanel user={user} onBuyNow={() => setActiveTab('cart')} />,
    bulk:        <BulkOrdersPanel onNavigate={setActiveTab} />,
    orders:      <OrdersPanel />,
    shop:        <ShopPanel user={user} onNavigate={setActiveTab} />,
    add_product: <AddProductPanel />,
    shop_orders: <MyShopOrdersPanel />,
    inquiries:   <InquiriesPanel />,
    payments:    <PaymentsPanel />,
    profile:     <ProfilePanel user={user} />,
  };

  return (
    <div className="flex min-h-screen bg-[#f4f6f4] font-body">
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/40 z-30 lg:hidden"
            onClick={() => setSidebarOpen(false)} />
      )}

      {/* ── Sidebar ──────────────────────────────────── */}
      <aside className={`fixed left-0 top-0 h-screen w-60 bg-white border-r
                  border-outline-variant/20 flex flex-col z-40 shadow-sm
                  transition-transform duration-300
                  ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
                  lg:translate-x-0`}>

        <div className="px-5 py-5 border-b border-outline-variant/10 hidden lg:block">
          <img src="/assets/elite-goats.png" alt="Elite Goats"
              className="h-10 w-auto object-contain" />
          <p className="text-[10px] font-bold text-outline uppercase tracking-widest mt-1">
            Butcher Portal
          </p>
        </div>
        <div className="pt-16 lg:hidden" /> {/* ← breathing room on mobile only */}

        <nav className="flex-1 px-3 mt-4 space-y-0.5 overflow-y-auto">
          {NAV.map((item) => (
            <button key={item.key} onClick={() => { setActiveTab(item.key); setSidebarOpen(false); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl
                          text-sm font-semibold transition-all ${
                active === item.key
                  ? 'bg-primary text-white shadow-sm'
                  : 'text-on-surface-variant hover:bg-surface-container hover:text-on-surface'
              }`}>
              <span className="material-symbols-outlined text-[20px]">{item.icon}</span>
              <span className="flex-1 text-left">{item.label}</span>

              {/* Cart badge */}
              {item.key === 'cart' && cart.length > 0 && (
                <span className={`text-[10px] font-black px-1.5 py-0.5 rounded-full ${
                  active === item.key ? 'bg-white/20 text-white' : 'bg-primary text-white'
                }`}>
                  {cart.length}
                </span>
              )}
            </button>
          ))}
        </nav>

        <div className="p-3 border-t border-outline-variant/10 space-y-1">
          <button onClick={() => navigate('/', { state: { viewHome: true } })}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs
                       font-medium text-on-surface-variant
                       hover:bg-surface-container transition-all">
            <span className="material-symbols-outlined text-[18px]">home</span>
            Back to Site
          </button>
          <button onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm
                       font-semibold text-red-600 hover:bg-red-50 transition-all">
            <span className="material-symbols-outlined text-[20px]">logout</span>
            Logout
          </button>
        </div>
      </aside>

      {/* ── Main ─────────────────────────────────────── */}
      <main className="flex-1 lg:ml-60 min-h-screen pt-20 px-4 lg:px-8 pb-8">

        {/* ── Top Header ───────────────────────────────── */}
        <header className="fixed top-0 left-0 lg:left-60 right-0 z-40 h-16 bg-white/80 backdrop-blur-md
                   border-b border-outline-variant/20 flex items-center
                   justify-between px-4 lg:px-8 gap-4">

          {/* Hamburger — mobile only */}
          <button className="lg:hidden mr-2" onClick={() => setSidebarOpen(true)}>
            <span className="material-symbols-outlined">menu</span>
          </button>      

          <div>
            <p className="text-sm font-black text-on-surface truncate max-w-[100px] sm:max-w-none">
            {NAV.find(n => n.key === active)?.label}
          </p>
            <p className="text-[10px] text-secondary">
              Welcome back, {user?.name?.split(' ')[0] || 'Butcher'} 🔪
            </p>
          </div>

          <div className="flex items-center gap-4 flex-shrink-0">

          {/* Notifications bell */}
          <div className="relative">
            <button
              onClick={async () => {
                setShowNotifs(!showNotifs);
                if (!showNotifs && user?.id) {
                  await api.patch('/api/butcher/notifications/read');
                  fetchNotifications();
                }
              }}
              className="relative p-2 text-on-surface-variant hover:bg-surface-container rounded-full transition-colors">
              <span className="material-symbols-outlined text-[22px]">notifications</span>
              {unreadCount > 0 && (
                <span className="absolute top-1 right-1 w-4 h-4 bg-red-500 rounded-full
                                text-white text-[10px] font-black flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </button>

            {showNotifs && (
              <div className="absolute right-0 top-12 w-80 bg-white rounded-2xl shadow-xl
                              border border-outline-variant/20 z-50 overflow-hidden">
                <div className="px-4 py-3 border-b border-outline-variant/10 flex justify-between items-center">
                  <span className="font-bold text-sm">Notifications</span>
                  <button onClick={async () => {
                    await api.patch('/api/butcher/notifications/read');
                    fetchNotifications();
                  }} className="text-[11px] text-outline hover:text-red-500">Clear all</button>
                </div>
                <div className="max-h-80 overflow-y-auto">
                  {notifList.length === 0 ? (
                    <p className="text-center text-outline text-sm py-8">No notifications</p>
                  ) : notifList.map(n => (
                    <div key={n.id}
                      className="px-4 py-3 border-b border-outline-variant/10 hover:bg-surface-container-low">
                      <p className="text-sm font-semibold text-on-surface">{n.title}</p>
                      <p className="text-xs text-outline mt-0.5">{n.body}</p>
                      <p className="text-[10px] text-outline-variant mt-1">
                        {new Date(n.created_at).toLocaleTimeString()}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="h-7 w-px bg-outline-variant/40" />

            {/* Quick cart button in header */}
            <button onClick={() => setActiveTab('cart')}
              className="relative flex items-center gap-2 px-4 py-2 rounded-full
                         border-2 border-primary text-primary font-bold text-sm
                         hover:bg-primary hover:text-white transition-all">
              <span className="material-symbols-outlined text-[18px]">shopping_cart</span>
              <span className="hidden md:inline">Cart</span>
              {cart.length > 0 && (
                <span className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full
                                 bg-primary text-white text-[10px] font-black
                                 flex items-center justify-center border-2 border-white">
                  {cart.length}
                </span>
              )}
            </button>

            <button onClick={() => setActiveTab('browse')}
              className="flex items-center gap-2 px-4 py-2 rounded-full bg-primary
                         text-white font-bold text-sm hover:opacity-90 transition-all shadow-sm">
              <span className="material-symbols-outlined text-[18px]">search</span>
              <span className="hidden sm:inline">Buy Animals</span>
            </button>

            <div className="h-7 w-px bg-outline-variant/40" />

            <div className="flex items-center gap-3">
              <div className="text-right hidden lg:block">
                <p className="text-sm font-bold text-on-surface leading-tight">
                  {user?.name || 'Butcher'}
                </p>
                <p className="text-[10px] text-secondary uppercase tracking-wider">
                  Butcher Account
                </p>
              </div>
              {user?.profile_image ? (
                <img src={user.profile_image} alt="Profile"
                    className="w-9 h-9 rounded-full object-cover border-2 border-red-100" />
              ) : (
                <div className="w-9 h-9 rounded-full bg-red-600 flex items-center
                                justify-center text-white font-black text-sm
                                border-2 border-red-100">
                  {(user?.name || 'B')[0].toUpperCase()}
                </div>
              )}
            </div>
          </div>
        </header>

        {/* ── Active panel ─────────────────────────────── */}
        {active === 'cart'
          ? <CartPanel
              apiBase="/api/customer/orders"
              onNavigate={setActiveTab}
              user={user}
            />
          : PANELS[active]
        }

      </main>
    </div>
  );
}